import React, { useCallback, useEffect } from "react";

export function useTextAreaPopup(
  containerRef,
  value,
  {
    focusOnMount = true,
    lineHeight = 19,
    verticalPadding = 60,
    initialLines = 3,
    scrollToTopOfTextarea = false,
    headerHeight = 70,
    scrollToBottomOnInit = true,
  } = {}
) {
  /**
   * @type React.RefObject<HTMLTextAreaElement> | null
   */
  const textArea = React.useRef();
  const [inFocus, setInFocus] = React.useState(false);

  const scrollToBottom = React.useCallback(
    function (delayed = false, targetScrollHeight = Number.MAX_SAFE_INTEGER) {
      if (containerRef && containerRef.current) {
        if (delayed) {
          setTimeout(() => {
            // In tests the container was already unmounted and then threw an error
            // that current is null
            if (containerRef && containerRef.current) {
              containerRef.current.scrollTop = targetScrollHeight;
            }
          }, 200);
        } else {
          containerRef.current.scrollTop = targetScrollHeight;
        }
      }
    },
    [containerRef]
  );

  const focusToTextArea = React.useCallback(
    function focusToTextArea() {
      if (textArea && textArea.current) {
        setTimeout(() => {
          if (textArea && textArea.current) {
            textArea.current.focus();

            if (value) {
              textArea.current.selectionStart = value.length;
              textArea.current.selectionEnd = value.length;
            }
          }
        }, 200);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onBlur = React.useCallback(() => {
    setTimeout(() => {
      if (textArea.current) {
        textArea.current.style.cssText = `height: ${
          lineHeight * initialLines + verticalPadding
        }px`;
      }
    }, 10);
  }, [textArea, lineHeight, verticalPadding, initialLines]);

  const onFocus = useCallback(() => {
    if (scrollToTopOfTextarea && containerRef.current && textArea.current) {
      containerRef.current.scrollTop = scrollToBottom(
        true,
        textArea.current.offsetTop - headerHeight - verticalPadding / 2
      );
    } else {
      scrollToBottom(true);
    }
  }, [
    scrollToBottom,
    containerRef,
    textArea,
    scrollToTopOfTextarea,
    headerHeight,
    verticalPadding,
  ]);

  useEffect(() => {
    if (focusOnMount) focusToTextArea();
    if (scrollToBottomOnInit) scrollToBottom(true);
  }, [focusOnMount, scrollToBottomOnInit, focusToTextArea, scrollToBottom]);

  // Solution for autosizing was found here: https://codepen.io/vsync/pen/frudD
  React.useEffect(() => {
    if (textArea.current) {
      const savedValue = textArea.current.value;
      textArea.current.value = "";
      textArea.current.baseScrollHeight = textArea.current.scrollHeight;
      textArea.current.value = savedValue;
    }
  }, [textArea]);

  React.useEffect(() => {
    function autosize() {
      const textAreaNode = textArea.current;
      const containerNode = containerRef.current;
      if (!textAreaNode) return;

      const prevHeight = textAreaNode.clientHeight;
      const prevScrollTop = containerNode ? containerNode.scrollTop : 0;
      const minRows = initialLines;

      textAreaNode.rows = minRows;
      const rows = Math.ceil(
        (textAreaNode.scrollHeight - minRows * lineHeight - verticalPadding) /
          lineHeight
      );
      textAreaNode.rows = Math.max(rows + minRows, 1);

      const heightDiff = textAreaNode.clientHeight - prevHeight;
      scrollToBottom(false, prevScrollTop + heightDiff);
    }

    function focus() {
      if (!textArea.current) return;
      textArea.current.style.cssText = "height:unset";
      setInFocus(true);
    }

    const node = textArea.current;
    if (!node) return;

    node.addEventListener("keyup", autosize);
    node.addEventListener("focus", autosize);
    node.addEventListener("focus", focus);

    return () => {
      if (!node) return;
      node.removeEventListener("keyup", autosize);
      node.removeEventListener("focus", autosize);
      node.removeEventListener("focus", focus);
    };
  }, [scrollToBottom, lineHeight, verticalPadding, containerRef, initialLines]);

  return {
    onFocus,
    onBlur,
    inFocus,
    textArea,
    scrollToBottom,
    focusToTextArea,
  };
}
